import type { CustomFieldDataType, CustomFieldDefinition } from '../types/custom-field-definitions.types';
import type { Contact, ContactAttributeValue } from '../types/contacts.types';
import { toContactStringValue } from './contact-attributes';

/** Attribute keys already rendered as dedicated form/table fields. */
export const HARDCODED_FORM_ATTRIBUTE_KEYS = new Set(['userCity', 'userPostalCode']);

/** Attribute keys already shown in the Personal Information section. */
export const CONTACT_DETAILS_DISPLAYED_ATTRIBUTE_KEYS = new Set(['registrationDate']);

/** Attribute keys that already have a dedicated static table column. */
export const HARDCODED_TABLE_ATTRIBUTE_KEYS = new Set(['userCity']);

const DATA_TYPE_VALUE_KEY: Record<CustomFieldDataType, keyof ContactAttributeValue> = {
  Text: 'StringValue',
  Number: 'NumberValue',
  NumberRange: 'NumberValue',
  Boolean: 'BoolValue',
  Date: 'DateValue',
  Dropdown: 'StringValue',
  MultiSelect: 'StringArrayValue',
};

const isKnownDataType = (value: string): value is CustomFieldDataType => value in DATA_TYPE_VALUE_KEY;

export const isEditableCustomFieldDefinition = (
  definition: CustomFieldDefinition
): definition is CustomFieldDefinition & { Key: string; Label: string; DataType: CustomFieldDataType } => {
  const key = definition.Key?.trim();
  const label = definition.Label?.trim();
  const dataType = definition.DataType?.trim();

  if (!key || !label || !dataType || definition.isDeleted) return false;
  if (HARDCODED_FORM_ATTRIBUTE_KEYS.has(key)) return false;
  return isKnownDataType(dataType);
};

export const getEditableCustomFieldDefinitions = (
  definitions: CustomFieldDefinition[] | undefined
): Array<CustomFieldDefinition & { Key: string; Label: string; DataType: CustomFieldDataType }> =>
  (definitions ?? []).filter(isEditableCustomFieldDefinition);

export const getContactAttributeByKey = (
  contact: Contact | undefined,
  propertyKey: string
): ContactAttributeValue | undefined =>
  contact?.Attributes?.find((entry) => entry?.PropertyKey === propertyKey) ?? undefined;

const readAttributeRawValue = (
  attribute: ContactAttributeValue | undefined,
  dataType: CustomFieldDataType
): unknown => {
  if (!attribute) return undefined;

  if (dataType === 'MultiSelect') {
    const arrayRaw = attribute.StringArrayValue;
    if (Array.isArray(arrayRaw) && arrayRaw.length > 0) {
      return arrayRaw;
    }
    // Legacy single-value enum stored as StringValue before multi-select mapping.
    const stringRaw = attribute.StringValue?.trim();
    return stringRaw ? [stringRaw] : undefined;
  }

  const valueKey = DATA_TYPE_VALUE_KEY[dataType];
  const raw = attribute[valueKey];
  if (raw === null || raw === undefined) return undefined;
  if (Array.isArray(raw) && raw.length === 0) return undefined;
  if (typeof raw === 'string' && raw.trim() === '') return undefined;
  return raw;
};

export const formatCustomFieldDisplayValue = (
  value: unknown,
  dataType: CustomFieldDataType
): string | undefined => {
  if (value === undefined || value === null) return undefined;

  if (dataType === 'Boolean') {
    return value === true ? 'Yes' : value === false ? 'No' : undefined;
  }

  if (dataType === 'MultiSelect' && Array.isArray(value)) {
    const parts = value
      .map((item) => toContactStringValue(item))
      .filter((item): item is string => Boolean(item));
    return parts.length > 0 ? parts.join(', ') : undefined;
  }

  if (dataType === 'Date' && typeof value === 'string') {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    // A date attribute is a calendar date stored as UTC midnight — formatting it in the browser's
    // timezone shows the previous day to every user west of UTC.
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  }

  return toContactStringValue(value);
};

export const getContactCustomFieldDisplayValue = (
  contact: Contact | undefined,
  definition: Pick<CustomFieldDefinition, 'Key' | 'DataType'>
): string | undefined => {
  const key = definition.Key?.trim();
  const dataType = definition.DataType?.trim();
  if (!key || !dataType || !isKnownDataType(dataType)) return undefined;

  const attribute = getContactAttributeByKey(contact, key);
  const raw = readAttributeRawValue(attribute, dataType);
  return formatCustomFieldDisplayValue(raw, dataType);
};

export const mapCustomFieldValueToFormString = (
  contact: Contact | undefined,
  definition: Pick<CustomFieldDefinition, 'Key' | 'DataType'>
): string => {
  const key = definition.Key?.trim();
  const dataType = definition.DataType?.trim();
  if (!key || !dataType || !isKnownDataType(dataType)) return '';

  const attribute = getContactAttributeByKey(contact, key);
  const raw = readAttributeRawValue(attribute, dataType);
  if (raw === undefined) return '';

  if (dataType === 'Boolean') return raw === true ? 'true' : raw === false ? 'false' : '';
  // <input type="date"> accepts only YYYY-MM-DD; the API returns an ISO instant
  // (1990-05-01T00:00:00Z), which the input silently renders as blank.
  if (dataType === 'Date' && typeof raw === 'string') return raw.slice(0, 10);
  if (dataType === 'MultiSelect' && Array.isArray(raw)) {
    return raw
      .map((item) => toContactStringValue(item))
      .filter((item): item is string => Boolean(item))
      .join(', ');
  }

  return toContactStringValue(raw) ?? '';
};

export const mapContactCustomFieldsToFormValues = (
  contact: Contact | undefined,
  definitions: CustomFieldDefinition[]
): Record<string, string> => {
  const values: Record<string, string> = {};

  getEditableCustomFieldDefinitions(definitions).forEach((definition) => {
    values[definition.Key] = mapCustomFieldValueToFormString(contact, definition);
  });

  return values;
};

const parseBooleanFormValue = (value: string): boolean | undefined => {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;
  return undefined;
};

const parseNumberFormValue = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseMultiSelectFormValue = (value: string): string[] | undefined => {
  const items = value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  return items.length > 0 ? items : undefined;
};

export const buildAttributeValueFromFormString = (
  definition: Pick<CustomFieldDefinition, 'Key' | 'DataType'>,
  formValue: string | undefined
): ContactAttributeValue | null => {
  const key = definition.Key?.trim();
  const dataType = definition.DataType?.trim();
  if (!key || !dataType || !isKnownDataType(dataType)) return null;

  const trimmed = formValue?.trim() ?? '';
  const valueKey = DATA_TYPE_VALUE_KEY[dataType];

  if (dataType === 'Boolean') {
    const boolValue = parseBooleanFormValue(trimmed);
    if (boolValue === undefined) return null;
    return { PropertyKey: key, DataType: dataType, [valueKey]: boolValue };
  }

  if (dataType === 'Number' || dataType === 'NumberRange') {
    const numberValue = parseNumberFormValue(trimmed);
    if (numberValue === undefined) return null;
    return { PropertyKey: key, DataType: dataType, [valueKey]: numberValue };
  }

  if (dataType === 'MultiSelect') {
    const arrayValue = parseMultiSelectFormValue(trimmed);
    if (!arrayValue) return null;
    return { PropertyKey: key, DataType: dataType, [valueKey]: arrayValue };
  }

  if (!trimmed) return null;
  return { PropertyKey: key, DataType: dataType, [valueKey]: trimmed };
};

export const buildAttributesFromCustomFieldFormValues = (
  definitions: CustomFieldDefinition[],
  formValues: Record<string, string> | undefined
): ContactAttributeValue[] =>
  getEditableCustomFieldDefinitions(definitions)
    .map((definition) => buildAttributeValueFromFormString(definition, formValues?.[definition.Key]))
    .filter((entry): entry is ContactAttributeValue => entry !== null);

export const mergeContactAttributes = (
  existing: ContactAttributeValue[] | null | undefined,
  updates: ContactAttributeValue[],
  managedKeys: string[]
): ContactAttributeValue[] => {
  const managedKeySet = new Set(managedKeys);
  const updateMap = new Map(
    updates
      .map((entry) => [entry.PropertyKey, entry] as const)
      .filter(([key]): key is [string, ContactAttributeValue] => Boolean(key))
  );

  const preserved = (existing ?? []).filter((entry) => {
    const key = entry.PropertyKey;
    return Boolean(key && !managedKeySet.has(key));
  });

  const nextManaged = managedKeys
    .map((key) => updateMap.get(key))
    .filter((entry): entry is ContactAttributeValue => Boolean(entry));

  return [...preserved, ...nextManaged];
};

export const getCustomFieldDefinitionsForColumns = (
  definitions: CustomFieldDefinition[] | undefined
): Array<CustomFieldDefinition & { Key: string; Label: string; DataType: CustomFieldDataType }> =>
  getEditableCustomFieldDefinitions(definitions).filter(
    (definition) => !HARDCODED_TABLE_ATTRIBUTE_KEYS.has(definition.Key)
  );

export const getContactAdditionalAttributesForDetails = (
  contact: Contact | undefined,
  definitions: CustomFieldDefinition[] | undefined
): Array<{
  definition: CustomFieldDefinition & { Key: string; Label: string; DataType: CustomFieldDataType };
  value: string | undefined;
}> =>
  getEditableCustomFieldDefinitions(definitions)
    .filter((definition) => !CONTACT_DETAILS_DISPLAYED_ATTRIBUTE_KEYS.has(definition.Key))
    .map((definition) => ({
      definition,
      value: getContactCustomFieldDisplayValue(contact, definition),
    }));

/** @deprecated Use getContactAdditionalAttributesForDetails — shows all fields, not only populated ones. */
export const getContactCustomFieldsForDetails = (
  contact: Contact | undefined,
  definitions: CustomFieldDefinition[] | undefined
) =>
  getContactAdditionalAttributesForDetails(contact, definitions).filter(
    (entry) => Boolean(entry.value?.length)
  );
